import { Battery50Icon, ChartBarIcon } from '@heroicons/react/20/solid';
import { BellIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { NOTIFICATION_VARIABLES } from '../../../hooks/useNotificationMessage';

// Mapa de variável → valor de exemplo para a pré-visualização
const VARIABLE_EXAMPLES = Object.fromEntries(
  NOTIFICATION_VARIABLES.map(v => [v.key, v.example]),
) as Record<string, string>;

function substituteVars(text: string): string {
  return text.replace(/\{\{(\w+)\}\}/g, (_, key: string) => VARIABLE_EXAMPLES[key] ?? `{{${key}}}`);
}

export interface NotificationMobilePreviewProps {
  readonly title: string;
  readonly body: string;
  readonly classname?: string;
  /** Substitui o heading padrão da pré-visualização */
  readonly heading?: string;
}

/**
 * Molecule de pré-visualização mobile da notificação.
 *
 * Renderiza um mockup de tela de bloqueio iOS com o título e corpo
 * da notificação, substituindo variáveis por valores de exemplo.
 */
export function NotificationMobilePreview({
  title,
  body,
  classname,
  heading,
}: NotificationMobilePreviewProps) {
  const { t } = useTranslation('notification');

  const previewTitle = substituteVars(title);
  const previewBody = substituteVars(body);

  return (
    <div
      className={[
        'flex w-[20rem] flex-shrink-0 flex-col gap-mini bg-ctx-layout-body p-mini rounded-lg',
        classname,
      ].join(' ')}
    >
      <p className="ts-heading-xs font-semibold tracking-wide text-ctx-content-title">
        {heading ?? t('wizard.message.preview.heading')}
      </p>

      {/* Frame do telefone */}
      <div className="overflow-hidden p-mini flex-1 min-h-0 min-h-[500px] rounded-lg bg-[#121926] border-thick border-[#1F2937] shadow-level3">
        {/* Barra de status */}
        <div className="flex items-center justify-between">
          <span className="ts-caption-xxs font-semibold text-base-neutral-white">9:41</span>
          {/* Ícones de sinal simplificados */}
          <div className="w-xl h-mini bg-base-gray-900 rounded-lg"></div>
          <div className="flex items-center gap-quark">
            <ChartBarIcon className="text-base-neutral-white w-micro h-micro" />
            <Battery50Icon className="text-base-neutral-white w-mini h-mini" />
          </div>
        </div>

        {/* Conteúdo da tela de bloqueio */}
        <div className="flex flex-col gap-mini pt-sm items-center">
          <div className="flex flex-col gap-nano items-center">
            <BellIcon className="text-base-brand-400 w-xxs" />
            <p className="text-center ts-caption-xxxs text-base-brand-400">
              {t('wizard.message.preview.lockscreen')}
            </p>
          </div>

          {/* Card da notificação */}
          <div className="w-full rounded-md bg-base-neutral-white/light px-mini py-mini">
            {/* Cabeçalho do card: ícone + app + tempo */}
            <div className="mb-quark flex  justify-between">
              <div className="flex items-center gap-quark">
                <div className="flex w-xs aspect-square flex-shrink-0 items-center justify-center rounded-sm bg-base-neutral-white">
                  <span className="ts-caption-xxxs font-bold leading-none text-base-gray-900">
                    M
                  </span>
                </div>
                <div className="flex flex-col gap-none justify-center ">
                  <p className="text-[10px] font-bold text-base-neutral-white leading-default">
                    {t('wizard.message.preview.appName')}
                  </p>
                  {/* Título */}
                  <p className="text-[10px] font-bold font-semibold leading-snug text-base-neutral-white">
                    {previewTitle}
                  </p>
                </div>
              </div>
              <span className="ts-paragraph-xxxs text-base-brand-400">
                {t('wizard.message.preview.timeAgo')}
              </span>
            </div>

            {/* Corpo */}
            <p className="text-[10px] mt-quark line-clamp-3 leading-snug text-base-neutral-white">
              {previewBody}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
